import { useEffect, useState } from 'react';
import { TextInput } from './ui.tsx';
import { getDateLocale } from '../i18n/locale.ts';
import {
  currencyInputDigits,
  DEFAULT_CURRENCY_CODE,
  formatEntryPlaceholder,
  roundToCurrencyStep,
} from '@shared/currency.ts';

/**
 * Money is stored and sent as integer cents (spec §8); people type dollars.
 * This keeps the two apart: local state holds exactly what was typed, so
 * intermediate values like "12." do not get reformatted mid-keystroke.
 *
 * Locale-aware for the same reason `inputToCents` below is: whatever this
 * renders has to survive being typed back in and re-parsed by that function,
 * and under a locale where `,` is the decimal mark, a plain `toFixed(2)`
 * ("65.00") would have its `.` read back as a *grouping* separator and
 * silently turn into 6,500.
 *
 * Currency-aware in one respect only: how many decimal places to offer.
 * "Cents" here always means hundredths of a major unit whatever the currency
 * (see shared/currency.ts), so parsing is the same arithmetic everywhere — but
 * a currency that has no decimals must not invite two, and one denominated in
 * thousandths must not invite a third the store cannot keep.
 */
export function centsToInput(
  cents: number | null | undefined,
  locale: string = getDateLocale(),
  currencyCode: string = DEFAULT_CURRENCY_CODE,
): string {
  if (cents === null || cents === undefined) return '';
  // The currency's *entry* precision, not its full precision: amounts are
  // stored in hundredths of a major unit, so a three-decimal currency has no
  // third box to offer (see currencyInputDigits).
  const digits = currencyInputDigits(currencyCode);
  // No grouping separators: this is an edit field, not a display label, and a
  // thousands separator here would just be another character `inputToCents`
  // has to strip back out. `minimum`/`maximumFractionDigits` pins it to
  // exactly the currency's decimal places the way `toFixed(2)` used to.
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: false,
    // The field is typed back in. A locale whose digits are not Latin
    // (ar-EG, bn-BD) would otherwise show a number this parser cannot read.
    numberingSystem: 'latn',
  }).format(cents / 100);
}

/**
 * Any decimal digit (٠, ২, ４) as ASCII 0–9. Numbering systems are contiguous
 * runs of ten, so the value is the distance from the zero that starts the run.
 */
function latinDigits(value: string): string {
  return value.replace(/\p{Nd}/gu, (digit) => {
    const point = digit.codePointAt(0);
    if (point === undefined) return digit;
    let zero = point;
    while (zero > 0 && /\p{Nd}/u.test(String.fromCodePoint(zero - 1))) zero -= 1;
    const offset = point - zero;
    return offset <= 9 ? String(offset) : digit;
  });
}

/**
 * The locale's decimal mark and group mark. `,` / `.` for English, the
 * reverse for Spanish, `٫` / `٬` for Arabic. Falls back to `.` and `,`.
 */
function separatorsFor(locale: string): { decimal: string; group: string | null } {
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(11111.1);
    return {
      decimal: parts.find((piece) => piece.type === 'decimal')?.value ?? '.',
      group: parts.find((piece) => piece.type === 'group')?.value ?? null,
    };
  } catch {
    return { decimal: '.', group: ',' };
  }
}

/**
 * Parses what someone typed into cents, honoring the *locale's* decimal
 * mark rather than assuming English conventions.
 *
 * Blindly stripping commas treats "12,50" — the ordinary way to type twelve
 * fifty on a Spanish decimal keyboard — as "1250", inflating a saved amount
 * 100x. Once the locale's decimal separator is known, everything else is
 * read as a grouping separator and discarded, and the decimal separator
 * itself is normalized to '.' before parsing.
 */
export function inputToCents(value: string, locale: string = getDateLocale()): number | null {
  const { decimal: decimalSeparator, group: localeGroup } = separatorsFor(locale);
  // A comma-decimal locale still has to accept a US numpad's ".", and the
  // other way around. The locale's own group mark (an Arabic ٬, a space) is
  // stripped as well, below.
  const groupSeparator = decimalSeparator === ',' ? '.' : ',';

  // A currency designator is stripped only where one legitimately appears —
  // leading or trailing — because the field shows whichever symbol the
  // company's currency uses, so "€12,50" and "KD 12.35" are what people
  // reasonably type or paste back in.
  //
  // Anything alphabetic left *inside* the number is not a designator, and
  // deleting it would silently invent an amount: "1e3" would become 13 and
  // "12usd34" would become 1234. This parser feeds quote prices, fees, visit
  // prices, charges and refunds, so a typo has to be refused rather than
  // reinterpreted.
  const withoutDesignator = latinDigits(value)
    .trim()
    .replace(/^[\p{Sc}\p{L}\s]+/u, '')
    .replace(/[\p{Sc}\p{L}\s]+$/u, '');
  if (/[\p{L}]/u.test(withoutDesignator)) return null;

  const compact = withoutDesignator.replace(/\s/gu, '');

  // One separator of either kind followed by exactly two digits is a decimal
  // mark, whatever the locale says: grouping never leaves a two-digit final
  // group, so "12.50" from a Spanish speaker on a US numpad (or "12,50"
  // pasted into an English locale) means twelve-fifty — reading it as a
  // thousands mark would inflate the amount 100×, and these amounts feed
  // charges and refunds. "1.250" keeps its three-digit group and stays
  // grouping, exactly as the locale reads it. The sign rides along: quote
  // fees legitimately go negative (credits), and "-12,50" dropping out of
  // this branch would fall through to grouping and inflate a pasted credit
  // the same 100×.
  const loneDecimal = /^([+-]?)(\d+)([.,])(\d{2})$/.exec(compact);
  if (loneDecimal) {
    return Math.round(Number(`${loneDecimal[1]}${loneDecimal[2]}.${loneDecimal[4]}`) * 100);
  }

  let normalized = compact.split(groupSeparator).join('');
  if (localeGroup && localeGroup !== decimalSeparator && localeGroup !== groupSeparator) {
    normalized = normalized.split(localeGroup).join('');
  }
  normalized = normalized.split(decimalSeparator).join('.');

  if (normalized === '') return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * 100);
}

interface MoneyInputProps {
  label: string;
  hint?: string;
  error?: string;
  valueCents: number | null;
  /** Decides the decimal places shown and the smallest step accepted. */
  currencyCode?: string;
  disabled?: boolean;
  min?: number;
  /** Shown while the box is empty. Defaults to an example amount in the currency. */
  placeholder?: string;
  onChangeCents: (cents: number | null) => void;
  /**
   * Told whether the box currently holds something that cannot be read as an
   * amount at all ("1e3", "12..3") — as opposed to being empty, which reports
   * the same `null` cents but means the opposite.
   *
   * Only worth wiring up where those two have different consequences. On a
   * field whose empty state means "no answer, carry on", they do: without
   * this, an unreadable entry is indistinguishable from an untouched one and
   * the form proceeds as though nobody had typed anything.
   */
  onInvalidChange?: (invalid: boolean) => void;
}

export function MoneyInput({
  label,
  hint,
  error,
  valueCents,
  currencyCode = DEFAULT_CURRENCY_CODE,
  disabled,
  min,
  placeholder,
  onChangeCents,
  onInvalidChange,
}: MoneyInputProps) {
  // Resolved once and threaded through every format/parse call below, rather
  // than each call defaulting it independently — the exact locale value
  // doesn't change mid-render, but display and parsing must always agree on
  // it, and passing it explicitly is what guarantees that rather than assumes it.
  const locale = getDateLocale();
  const [text, setText] = useState(() => centsToInput(valueCents, locale, currencyCode));
  const [editing, setEditing] = useState(false);

  // Adopt a value changed elsewhere (a recalculation, a reset) without
  // clobbering what the user is currently typing. What was typed is compared
  // *after* stepping, because that is what it was reported as: typing "12.3"
  // under a whole-unit currency reports 12, and reformatting the field to "12"
  // on the very next keystroke would make the decimal point untypeable. It
  // still tidies up on blur. For an ordinary currency the step is one cent, so
  // this is the same comparison as before.
  //
  // Nothing is adopted at all while the field has focus. Emptying a field
  // reports null, most callers store that as 0, and the comparison below then
  // reads "" as not matching 0 and writes "0.00" back into the box — so
  // clearing $65.00 to type $75.00 put a zero in the way, which is exactly the
  // fight this component is meant to prevent. The blur below re-syncs.
  useEffect(() => {
    if (editing) return;
    setText((current) => {
      const typed = inputToCents(current, locale);
      const matches = typed === null ? valueCents === null : roundToCurrencyStep(typed, currencyCode) === valueCents;
      return matches ? current : centsToInput(valueCents, locale, currencyCode);
    });
  }, [valueCents, locale, currencyCode, editing]);

  return (
    <TextInput
      label={label}
      hint={hint}
      error={error}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      disabled={disabled}
      value={text}
      placeholder={placeholder ?? formatEntryPlaceholder(currencyCode, locale)}
      onChange={(event) => {
        setText(event.target.value);
        const cents = inputToCents(event.target.value, locale);
        // Empty and unreadable both report `null` cents, and a caller that
        // treats null as "not answered" needs to be able to tell them apart.
        onInvalidChange?.(cents === null && event.target.value.trim() !== '');
        if (cents === null) {
          onChangeCents(null);
        } else {
          // Snapped to something the currency can express: a currency billed
          // in whole units has no ¥12.34, and letting one be entered only
          // moves the rejection to the moment the card is charged.
          const stepped = roundToCurrencyStep(cents, currencyCode);
          onChangeCents(min !== undefined ? Math.max(min, stepped) : stepped);
        }
      }}
      onFocus={() => setEditing(true)}
      onBlur={() => {
        setEditing(false);
        // Tidy up what was understood; keep what was not. Text that parses to
        // nothing used to be wiped here, which is the one case where wiping
        // is worst: the entry silently disappears, the field reads as an
        // ordinary empty box, and whatever "empty" means to the form around
        // it happens instead of what the person typed. The re-sync effect
        // above already leaves unreadable text alone; this now agrees with it.
        if (text.trim() !== '' && inputToCents(text, locale) === null) return;
        setText(centsToInput(valueCents, locale, currencyCode));
      }}
    />
  );
}
